// src/pages/inventory/InventoryWarehouses.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Alert,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
    MenuItem
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Warehouse as WarehouseIcon,
    LocationOn as LocationIcon
} from '@mui/icons-material';

const InventoryWarehouses = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [warehouses, setWarehouses] = useState([]);
    const [users, setUsers] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentWarehouse, setCurrentWarehouse] = useState({
        name: '',
        location: '',
        responsible_user_id: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            // Fetch warehouses
            const warehousesResponse = await fetch('/api/inventory/warehouses', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!warehousesResponse.ok) {
                throw new Error('Error al obtener almacenes');
            }

            const warehousesData = await warehousesResponse.json();
            setWarehouses(warehousesData);

            // Fetch users (potential warehouse managers)
            const usersResponse = await fetch('/api/users', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (usersResponse.ok) {
                const usersData = await usersResponse.json();
                setUsers(usersData);
            }

            setError(null);
        } catch (error) {
            console.error('Error fetching data:', error);
            setError(error.message || 'Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (warehouse = null) => {
        if (warehouse) {
            setCurrentWarehouse({
                id: warehouse.id,
                name: warehouse.name || '',
                location: warehouse.location || '',
                responsible_user_id: warehouse.responsible_user_id || ''
            });
            setIsEditing(true);
        } else {
            setCurrentWarehouse({
                name: '',
                location: '',
                responsible_user_id: ''
            });
            setIsEditing(false);
        }
        setDialogOpen(true);
        setError(null);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setError(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentWarehouse(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        try {
            if (!currentWarehouse.name.trim()) {
                setError('El nombre del almacén es obligatorio');
                return;
            }

            setLoading(true);
            const token = localStorage.getItem('token');

            const method = isEditing ? 'PUT' : 'POST';
            const url = isEditing
                ? `/api/inventory/warehouses/${currentWarehouse.id}`
                : '/api/inventory/warehouses';

            // Preparar datos para enviar
            const dataToSend = {
                name: currentWarehouse.name.trim(),
                location: currentWarehouse.location.trim() || null,
                responsible_user_id: currentWarehouse.responsible_user_id || null
            };

            console.log('Sending data:', dataToSend); // Para debug

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(dataToSend)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Error al ${isEditing ? 'actualizar' : 'crear'} almacén`);
            }

            setSuccess(`Almacén ${isEditing ? 'actualizado' : 'creado'} exitosamente`);
            setTimeout(() => setSuccess(null), 5000);

            handleCloseDialog();
            await fetchData();
        } catch (error) {
            console.error('Error submitting warehouse:', error);
            setError(error.message || `Error al ${isEditing ? 'actualizar' : 'crear'} almacén`);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Está seguro de eliminar este almacén? Esta acción no se puede deshacer.')) {
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const response = await fetch(`/api/inventory/warehouses/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al eliminar almacén');
            }

            setSuccess('Almacén eliminado exitosamente');
            setTimeout(() => setSuccess(null), 5000);

            await fetchData();
        } catch (error) {
            console.error('Error deleting warehouse:', error);
            setError(error.message || 'Error al eliminar almacén');
        } finally {
            setLoading(false);
        }
    };

    if (loading && warehouses.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
                <WarehouseIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Almacenes de Inventario
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    disabled={loading}
                >
                    Nuevo Almacén
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Nombre</TableCell>
                            <TableCell>Ubicación</TableCell>
                            <TableCell>Responsable</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {warehouses.length > 0 ? (
                            warehouses.map((warehouse) => (
                                <TableRow key={warehouse.id}>
                                    <TableCell>{warehouse.name}</TableCell>
                                    <TableCell>{warehouse.location || '-'}</TableCell>
                                    <TableCell>
                                        {warehouse.responsible_user_name ||
                                            users.find(u => u.id === warehouse.responsible_user_id)?.username ||
                                            '-'}
                                    </TableCell>
                                    <TableCell>{warehouse.status || 'active'}</TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            color="primary"
                                            size="small"
                                            onClick={() => handleOpenDialog(warehouse)}
                                            disabled={loading}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() => handleDelete(warehouse.id)}
                                            disabled={loading}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    No hay almacenes disponibles
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Dialog for Add/Edit */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {isEditing ? 'Editar Almacén' : 'Nuevo Almacén'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Nombre"
                                    name="name"
                                    value={currentWarehouse.name}
                                    onChange={handleInputChange}
                                    error={!currentWarehouse.name.trim() && error}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Ubicación"
                                    name="location"
                                    value={currentWarehouse.location}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Responsable"
                                    name="responsible_user_id"
                                    value={currentWarehouse.responsible_user_id}
                                    onChange={handleInputChange}
                                >
                                    <MenuItem value="">Ninguno</MenuItem>
                                    {users.map((user) => (
                                        <MenuItem key={user.id} value={user.id}>
                                            {user.username} - {user.first_name} {user.last_name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={loading}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={!currentWarehouse.name.trim() || loading}
                    >
                        {loading ? <CircularProgress size={20} /> : (isEditing ? 'Actualizar' : 'Guardar')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InventoryWarehouses;